import { useEffect, useMemo, useState } from "react";
import axios from "../api/axios";
import { buildPermissionGroups, formatPermissionLabel, type PermissionGroupView } from "../constants/permissionLabels";

type RoleOption = {
  id: string;
  name: string;
};

type PermissionDto = {
  id: string;
  code: string;
};

export default function RolePermissionManager() {
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [permissions, setPermissions] = useState<PermissionDto[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [rolePermissions, setRolePermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [roleRes, permRes] = await Promise.all([axios.get("/roles"), axios.get("/permission")]);
        setRoles(roleRes.data ?? []);

        const uniquePermissions = Array.from(
          new Map<string, PermissionDto>((permRes.data ?? []).map((p: PermissionDto) => [p.code, p])),
        ).map(([, value]) => value);
        setPermissions(uniquePermissions);
      } catch (error) {
        console.error("[permissions] roller / izinler alınamadı", error);
      } finally {
        setLoading(false);
      }
    };

    void fetchInitialData();
  }, []);

  useEffect(() => {
    if (!selectedRoleId) {
      setRolePermissions([]);
      return;
    }

    const fetchRolePermissions = async () => {
      try {
        const response = await axios.get(`/permission/role/${selectedRoleId}`);
        setRolePermissions((response.data ?? []).map((p: PermissionDto) => p.code));
      } catch (error) {
        console.error("[permissions] rol izinleri alınamadı", error);
        setRolePermissions([]);
      }
    };

    void fetchRolePermissions();
  }, [selectedRoleId]);

  const handleToggle = async (code: string) => {
    if (!selectedRoleId) {
      return;
    }

    const currentlyAssigned = rolePermissions.includes(code);

    try {
      if (currentlyAssigned) {
        const response = await axios.get(`/permission/role/${selectedRoleId}`);
        const match = (response.data ?? []).find((item: PermissionDto) => item.code === code);
        if (match) {
          await axios.delete(`/permission/${match.id}`);
        }
      } else {
        await axios.post("/permission", { roleId: selectedRoleId, code });
      }

      const refresh = await axios.get(`/permission/role/${selectedRoleId}`);
      setRolePermissions((refresh.data ?? []).map((p: PermissionDto) => p.code));
    } catch (error) {
      console.error("[permissions] toggle başarısız", error);
    }
  };

  const permissionGroups: PermissionGroupView[] = useMemo(
    () => buildPermissionGroups(permissions.map((perm) => perm.code)),
    [permissions],
  );

  return (
    <div className="mt-4 rounded bg-white p-4 shadow">
      <h2 className="mb-4 text-lg font-semibold">ğŸ›¡ï¸ Rol İzin Eşleştirme</h2>

      {loading ? (
        <div className="text-sm text-gray-500">Yükleniyor...</div>
      ) : (
        <>
          <select
            className="mb-4 w-full rounded border p-2"
            value={selectedRoleId}
            onChange={(event) => setSelectedRoleId(event.target.value)}
          >
            <option value="">Rol Seçin</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>

          {selectedRoleId && (
            <div className="space-y-4">
              {permissionGroups.length === 0 && (
                <div className="text-sm text-gray-500">Tanımlı izin bulunamadı.</div>
              )}
              {permissionGroups.map((group) => (
                <div key={group.title} className="rounded border border-slate-200 p-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{group.title}</div>
                  <div className="mt-2 grid gap-2 md:grid-cols-2">
                    {group.items.map((perm) => (
                      <label
                        key={perm.code}
                        className="flex items-center space-x-2 rounded border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={rolePermissions.includes(perm.code)}
                          onChange={() => handleToggle(perm.code)}
                        />
                        <span>{formatPermissionLabel(perm.code)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
